import React, { useCallback } from 'react';
import toast from 'react-hot-toast';

import useBookmarkCreate from '@hooks/useBookmarkCreate';
import useBookmarkDestroy from '@hooks/useBookmarkDestroy';
import useBookmarks from '@hooks/useBookmarks';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { bookmarks_path } from '@routes';
import { ClickTarget, ContentType, TrackingLocation, click } from '@tracking';
import { BookmarkableType } from '@utils/backend-api/bookmarks';

import BookmarkAddedToast from './BookmarkAddedToast';
import {
  BOOKMARKED_TOOLTIP_MESSAGE,
  BOOKMARKS,
  BOOKMARK_TOAST_ID,
  NOT_BOOKMARKED_TOAST_MESSAGE,
  NOT_BOOKMARKED_TOOLTIP_MESSAGE,
  getBookmarkErrorMessage,
} from './constants';

export default function useBookmarkToggle(
  contentType: ContentType,
  contentId: number,
  enabled = true
) {
  const { trackEvent } = useEventTracking({
    contentId,
    contentType,
  });
  const { trackEvent: trackEventWithLocation } = useEventTracking({
    contentId,
    contentType,
    location: TrackingLocation.Toast,
  });
  const hydrated = useServerSideRendering();
  const { isBookmarked, bookmarkableType, isBookmarkStatusEnabled, isLoadingBookmarked, userId } =
    useBookmarks(contentType, contentId, enabled);
  const { createBookmarkAsync, isCreatingBookmark } = useBookmarkCreate({
    userId,
    bookmarkableType: bookmarkableType ?? BookmarkableType.Article,
    contentId,
  });
  const { destroyBookmarkAsync, isDestroyingBookmark } = useBookmarkDestroy({
    userId,
    bookmarkableType: bookmarkableType ?? BookmarkableType.Article,
    contentId,
  });

  const isPending = isLoadingBookmarked || isCreatingBookmark || isDestroyingBookmark;
  const tooltipMessage = isBookmarked ? BOOKMARKED_TOOLTIP_MESSAGE : NOT_BOOKMARKED_TOOLTIP_MESSAGE;
  const isDisabled = isPending || !isBookmarkStatusEnabled || !hydrated;

  const handleToastClick = useCallback(() => {
    trackEventWithLocation(
      click({
        clickTarget: ClickTarget.AnchorLink,
        clickText: BOOKMARKS,
        clickLink: bookmarks_path(),
      })
    );
  }, [trackEventWithLocation]);

  const handleBookmarkClick = useCallback(async () => {
    if (!bookmarkableType || !isBookmarkStatusEnabled || isPending) {
      return;
    }

    try {
      if (isBookmarked) {
        await destroyBookmarkAsync();
        toast.success(NOT_BOOKMARKED_TOAST_MESSAGE, {
          id: BOOKMARK_TOAST_ID,
        });
      } else {
        await createBookmarkAsync();
        toast.success(<BookmarkAddedToast onToastClick={handleToastClick} />, {
          id: BOOKMARK_TOAST_ID,
        });
      }

      trackEvent(click({ clickTarget: ClickTarget.Bookmark, clickText: tooltipMessage }));
    } catch (error) {
      toast.error(getBookmarkErrorMessage(error), {
        id: BOOKMARK_TOAST_ID,
      });
    }
  }, [
    bookmarkableType,
    isBookmarkStatusEnabled,
    isPending,
    isBookmarked,
    destroyBookmarkAsync,
    createBookmarkAsync,
    handleToastClick,
    trackEvent,
    tooltipMessage,
  ]);

  return {
    hydrated,
    isBookmarked,
    isDisabled,
    tooltipMessage,
    handleBookmarkClick,
  };
}
